import React, { useMemo } from 'react'
import type { DbHighlight } from '../lib/supabase'

interface Props {
  content: string
  highlights: DbHighlight[]
  onHighlightClick: (highlight: DbHighlight) => void
}

export function HighlightedContent({
  content,
  highlights,
  onHighlightClick,
}: Props) {
  const segments = useMemo(() => {
    if (!highlights.length) {
      return [{ text: content, highlight: null, key: '0' }]
    }

    const result: Array<{
      text: string
      highlight: DbHighlight | null
      key: string
    }> = []
    let lastEnd = 0

    // Sort by start offset to ensure proper ordering
    const sorted = [...highlights].sort(
      (a, b) => a.start_offset - b.start_offset
    )

    for (const h of sorted) {
      // Skip invalid highlights (out of bounds)
      if (h.start_offset >= content.length || h.end_offset > content.length) {
        continue
      }

      // Add unmarked text before this highlight
      if (h.start_offset > lastEnd) {
        result.push({
          text: content.slice(lastEnd, h.start_offset),
          highlight: null,
          key: `plain-${lastEnd}`,
        })
      }

      // Handle overlapping highlights - skip if overlaps with previous
      if (h.start_offset < lastEnd) {
        continue
      }

      // Add highlighted text
      result.push({
        text: content.slice(h.start_offset, h.end_offset),
        highlight: h,
        key: `hl-${h.id}`,
      })

      lastEnd = Math.max(lastEnd, h.end_offset)
    }

    // Add remaining text after last highlight
    if (lastEnd < content.length) {
      result.push({
        text: content.slice(lastEnd),
        highlight: null,
        key: `plain-${lastEnd}`,
      })
    }

    return result
  }, [content, highlights])

  return (
    <>
      {segments.map(({ text, highlight, key }) =>
        highlight ? (
          <mark
            key={key}
            className="text-highlight"
            style={{ backgroundColor: highlight.color }}
            onClick={(e) => {
              e.stopPropagation()
              onHighlightClick(highlight)
            }}
            data-highlight-id={highlight.id}
          >
            {text}
            {highlight.note && (
              <span className="highlight-note-indicator" title="Has note">
                💬
              </span>
            )}
          </mark>
        ) : (
          <React.Fragment key={key}>{text}</React.Fragment>
        )
      )}
    </>
  )
}
